import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { BiSolidInbox } from 'react-icons/bi';
import { useAuth } from '@/auth/AuthContext';
import { hasRole, SETTINGS_FINANCE_ROLES } from '@/auth/roles';
import { getExamPricing, getExamPricingHistory, setExamPrice } from '@/api/finance.api';
import { ApiError } from '@/api/errors';
import { StatusChip } from '@/components/common/StatusChip';
import { FormField } from '@/components/form/FormField';
import { DataTable, type DataTableColumn } from '@/components/table/DataTable';
import { EmptyState } from '@/components/table/EmptyState';
import { formatCurrencyEGP, formatCurrencyUsd, formatDateOnly } from '@/lib/format';
import { showSuccessToast } from '@/lib/toast';
import { EXAM_SECTIONS } from '@/types/shared';
import type { ExamPriceRow, ExamPriceVersion, ExamSection } from '@/types/shared';
import './ExamPricingManager.css';

interface FieldErrors {
  costAmount?: string;
  priceAmount?: string;
}

function formatNative(
  amount: string | undefined,
  currency: 'USD' | 'EGP' | undefined,
  language: string,
): string {
  if (amount == null) return '';
  const num = Number(amount);
  return currency === 'USD' ? formatCurrencyUsd(num, language) : formatCurrencyEGP(num, language);
}

/**
 * Exam Pricing tab (`/settings/finance`, tab 1) — 17-finance-ux.md §4. Fixed set of 4 inner
 * section tabs (SAT/ACT/EST I/EST II, never admin-extensible), reusing the exact `LookupsPage.tsx`
 * ARIA-tabs pattern. A single `GET /finance/exam-pricing` call fetches all 4 sections' current
 * pricing at once (§4.4/§10.6 — one loading state for the whole tab, not 4 independent ones).
 */
export function ExamPricingManager() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const canEdit = hasRole(user?.role, SETTINGS_FINANCE_ROLES);
  const isRtl = i18n.language.startsWith('ar');

  const [sections, setSections] = useState<ExamPriceRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const [activeIndex, setActiveIndex] = useState(0);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const [editOpen, setEditOpen] = useState(false);
  const [costInput, setCostInput] = useState('');
  const [priceInput, setPriceInput] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<ExamPriceVersion[] | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    getExamPricing()
      .then((res) => {
        if (!cancelled) setSections(res.sections);
      })
      .catch(() => {
        if (!cancelled) setLoadError(t('financeSettings.examPricing.loadError'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadToken, t]);

  function refetch() {
    setReloadToken((v) => v + 1);
  }

  function focusTab(index: number) {
    const normalized = (index + EXAM_SECTIONS.length) % EXAM_SECTIONS.length;
    selectSectionTab(normalized);
    tabRefs.current[normalized]?.focus();
  }

  function handleTabKeyDown(e: KeyboardEvent<HTMLButtonElement>, index: number) {
    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault();
        focusTab(index + (isRtl ? -1 : 1));
        break;
      case 'ArrowLeft':
        e.preventDefault();
        focusTab(index + (isRtl ? 1 : -1));
        break;
      case 'Home':
        e.preventDefault();
        focusTab(0);
        break;
      case 'End':
        e.preventDefault();
        focusTab(EXAM_SECTIONS.length - 1);
        break;
      default:
        break;
    }
  }

  function selectSectionTab(index: number) {
    setActiveIndex(index);
    setEditOpen(false);
    setHistoryOpen(false);
    setFieldErrors({});
    setSaveError(null);
  }

  const activeSection: ExamSection = EXAM_SECTIONS[activeIndex];
  const row = sections?.find((s) => s.examSection === activeSection) ?? null;

  function openEdit() {
    if (!row) return;
    setHistoryOpen(false);
    setCostInput(row.costAmount);
    setPriceInput(row.priceAmount);
    setFieldErrors({});
    setSaveError(null);
    setEditOpen(true);
  }

  function closeEdit() {
    setEditOpen(false);
    setFieldErrors({});
    setSaveError(null);
  }

  function isValidAmount(value: string): boolean {
    if (value.trim() === '') return false;
    const num = Number(value);
    return !Number.isNaN(num) && num >= 0;
  }

  // Live, non-blocking price<cost check — mirrors the server's own rule (§A §8) so the two should
  // never disagree in practice; this is what drives `FormField`'s amber `warning` state, never `error`.
  const priceBelowCost =
    isValidAmount(costInput) && isValidAmount(priceInput) && Number(priceInput) < Number(costInput);

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!row) return;
    setSaveError(null);

    const errors: FieldErrors = {};
    if (!isValidAmount(costInput)) {
      errors.costAmount = t('financeSettings.examPricing.errors.costInvalid');
    }
    if (!isValidAmount(priceInput)) {
      errors.priceAmount = t('financeSettings.examPricing.errors.priceInvalid');
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSaving(true);
    try {
      const updated = await setExamPrice(activeSection, {
        costAmount: costInput.trim(),
        priceAmount: priceInput.trim(),
      });
      setSections((prev) =>
        prev ? prev.map((s) => (s.examSection === activeSection ? updated : s)) : prev,
      );
      showSuccessToast(t('financeSettings.examPricing.saveSuccess'));
      setEditOpen(false);
      // A save appends a new version — drop any already-fetched history so the next "History" open
      // re-fetches and includes it (§2.1 step 4: the just-superseded version now lists as a past row).
      setHistory(null);
    } catch (err) {
      const apiError = ApiError.fromUnknown(err);
      const errs: FieldErrors = {};
      for (const detail of apiError.details) {
        if (detail.field === 'costAmount') errs.costAmount = detail.message;
        if (detail.field === 'priceAmount') errs.priceAmount = detail.message;
      }
      if (Object.keys(errs).length > 0) {
        setFieldErrors(errs);
      } else {
        setSaveError(
          apiError.code === 'NETWORK_ERROR'
            ? t('financeSettings.examPricing.networkError')
            : apiError.message || t('financeSettings.examPricing.genericError'),
        );
      }
    } finally {
      setSaving(false);
    }
  }

  function fetchHistory() {
    setHistoryLoading(true);
    setHistoryError(null);
    getExamPricingHistory()
      .then((res) => setHistory(res.versions))
      .catch(() => setHistoryError(t('financeSettings.examPricing.history.loadError')))
      .finally(() => setHistoryLoading(false));
  }

  function toggleHistory() {
    setEditOpen(false);
    setHistoryOpen((v) => {
      const next = !v;
      if (next && history === null) fetchHistory();
      return next;
    });
  }

  const sectionHistory = (history ?? []).filter((v) => v.examSection === activeSection);

  const historyColumns: DataTableColumn<ExamPriceVersion>[] = [
    {
      key: 'effectiveFrom',
      header: t('financeSettings.examPricing.history.columns.effectiveFrom'),
      render: (v) => <bdi>{formatDateOnly(v.effectiveFrom, i18n.language)}</bdi>,
    },
    {
      key: 'cost',
      header: t('financeSettings.examPricing.history.columns.cost'),
      render: (v) => <bdi>{formatNative(v.costAmount, v.currency, i18n.language)}</bdi>,
    },
    {
      key: 'price',
      header: t('financeSettings.examPricing.history.columns.price'),
      render: (v) => <bdi>{formatNative(v.priceAmount, v.currency, i18n.language)}</bdi>,
    },
    {
      key: 'current',
      header: t('financeSettings.examPricing.history.columns.current'),
      render: (v) =>
        v.isCurrent ? (
          <StatusChip
            label={t('financeSettings.examPricing.history.currentBadge')}
            tone="success"
          />
        ) : null,
    },
  ];

  return (
    <div className="fa-exam-pricing">
      <div
        role="tablist"
        aria-label={t('financeSettings.examPricing.sectionsTabsAriaLabel')}
        className="fa-exam-pricing__tablist"
      >
        {EXAM_SECTIONS.map((section, index) => (
          <button
            key={section}
            ref={(el) => {
              tabRefs.current[index] = el;
            }}
            type="button"
            role="tab"
            id={`exam-pricing-tab-${section.replace(/\s+/g, '-')}`}
            aria-selected={activeIndex === index}
            aria-controls={`exam-pricing-tabpanel-${section.replace(/\s+/g, '-')}`}
            tabIndex={activeIndex === index ? 0 : -1}
            className={`fa-exam-pricing__tab${
              activeIndex === index ? ' fa-exam-pricing__tab--active' : ''
            }`}
            onClick={() => selectSectionTab(index)}
            onKeyDown={(e) => handleTabKeyDown(e, index)}
          >
            {t(`financeSettings.examPricing.sections.${section}`)}
          </button>
        ))}
      </div>

      <div
        role="tabpanel"
        id={`exam-pricing-tabpanel-${activeSection.replace(/\s+/g, '-')}`}
        aria-labelledby={`exam-pricing-tab-${activeSection.replace(/\s+/g, '-')}`}
        tabIndex={0}
        className="fa-exam-pricing__tabpanel"
      >
        {loadError && (
          <div className="fa-exam-pricing__error" role="alert">
            <p>{loadError}</p>
            <button type="button" className="fa-btn fa-btn--secondary" onClick={refetch}>
              {t('common.retry')}
            </button>
          </div>
        )}

        {!loadError && (
          <div className="fa-pricing-card">
            {loading || !row ? (
              <div
                className="fa-pricing-card__skeleton"
                aria-label={t('financeSettings.examPricing.loadingAriaLabel')}
              >
                <span className="fa-pricing-card__skeleton-line" />
                <span className="fa-pricing-card__skeleton-line" />
                <span className="fa-pricing-card__skeleton-line" />
                <span className="fa-pricing-card__skeleton-line" />
              </div>
            ) : (
              <>
                <div className="fa-pricing-card__row">
                  <span className="fa-pricing-card__label">
                    {t('financeSettings.examPricing.currencyLabel')}
                  </span>
                  <StatusChip label={row.currency} tone="neutral" />
                </div>
                <div className="fa-pricing-card__row">
                  <span className="fa-pricing-card__label">
                    {t('financeSettings.examPricing.costLabel')}
                  </span>
                  <span className="fa-pricing-card__value fa-ltr-isolate">
                    <bdi>{formatNative(row.costAmount, row.currency, i18n.language)}</bdi>
                  </span>
                </div>
                <div className="fa-pricing-card__row">
                  <span className="fa-pricing-card__label">
                    {t('financeSettings.examPricing.priceLabel')}
                  </span>
                  <span className="fa-pricing-card__value fa-ltr-isolate">
                    <bdi>{formatNative(row.priceAmount, row.currency, i18n.language)}</bdi>
                  </span>
                </div>
                <div className="fa-pricing-card__row">
                  <span className="fa-pricing-card__label">
                    {t('financeSettings.examPricing.effectiveSinceLabel')}
                  </span>
                  <span className="fa-pricing-card__value">
                    <bdi>{formatDateOnly(row.effectiveFrom, i18n.language)}</bdi>
                  </span>
                </div>

                {canEdit && (
                  <div className="fa-pricing-card__actions">
                    <button
                      type="button"
                      className="fa-btn fa-btn--secondary"
                      onClick={editOpen ? closeEdit : openEdit}
                    >
                      {editOpen
                        ? t('financeSettings.examPricing.cancelButton')
                        : t('financeSettings.examPricing.editButton')}
                    </button>
                    <button
                      type="button"
                      className="fa-btn fa-btn--ghost"
                      aria-expanded={historyOpen}
                      onClick={toggleHistory}
                    >
                      {t('financeSettings.examPricing.historyButton')}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {editOpen && row && (
          <form className="fa-form-section" onSubmit={handleSave} noValidate>
            <h2 className="fa-form-section__title">
              {t(`financeSettings.examPricing.sections.${activeSection}`)}
            </h2>
            <div className="fa-field-row">
              <FormField
                id="exam-pricing-cost"
                label={t('financeSettings.examPricing.costLabel')}
                required
                error={fieldErrors.costAmount}
              >
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="fa-ltr-isolate"
                  value={costInput}
                  disabled={saving}
                  onChange={(e) => {
                    setCostInput(e.target.value);
                    setFieldErrors((prev) => ({ ...prev, costAmount: undefined }));
                  }}
                />
              </FormField>
              <FormField
                id="exam-pricing-price"
                label={t('financeSettings.examPricing.priceLabel')}
                required
                error={fieldErrors.priceAmount}
                warning={
                  !fieldErrors.priceAmount && priceBelowCost
                    ? t('financeSettings.examPricing.priceBelowCostWarning')
                    : undefined
                }
              >
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="fa-ltr-isolate"
                  value={priceInput}
                  disabled={saving}
                  onChange={(e) => {
                    setPriceInput(e.target.value);
                    setFieldErrors((prev) => ({ ...prev, priceAmount: undefined }));
                  }}
                />
              </FormField>
            </div>
            <div className="fa-form-actions">
              <button
                type="button"
                className="fa-btn fa-btn--secondary"
                disabled={saving}
                onClick={closeEdit}
              >
                {t('financeSettings.examPricing.cancelButton')}
              </button>
              <button type="submit" className="fa-btn fa-btn--primary" disabled={saving}>
                {saving
                  ? t('financeSettings.examPricing.savingButton')
                  : t('financeSettings.examPricing.saveButton')}
              </button>
            </div>
            {saveError && (
              <p className="fa-field__error" role="alert">
                {saveError}
              </p>
            )}
          </form>
        )}

        {historyOpen && (
          <div className="fa-pricing-history">
            <h2 className="fa-pricing-history__title">
              {t('financeSettings.examPricing.history.title', {
                section: t(`financeSettings.examPricing.sections.${activeSection}`),
              })}
            </h2>
            <DataTable
              columns={historyColumns}
              rows={sectionHistory}
              rowKey={(v) => v.versionId}
              caption={t('financeSettings.examPricing.history.tableCaption', {
                section: t(`financeSettings.examPricing.sections.${activeSection}`),
              })}
              meta={null}
              loading={historyLoading}
              error={historyError}
              onRetry={fetchHistory}
              onPageChange={() => {}}
              onPageSizeChange={() => {}}
              showExportButton={false}
              emptyState={
                <EmptyState
                  icon={BiSolidInbox}
                  title={t('financeSettings.examPricing.history.empty.title')}
                  hint={t('financeSettings.examPricing.history.empty.hint')}
                />
              }
            />
          </div>
        )}
      </div>
    </div>
  );
}
